import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook for detecting when all 4 corners of an element are pressed simultaneously
 * for a specified duration. Designed for mobile touch devices.
 * 
 * @param {number} holdDuration - Time in ms to hold before triggering (default: 1000ms)
 * @param {number} cornerThreshold - Distance from corner in px to count as a corner touch (default: 50px)
 * @returns {Object} - { onTouchStart, onTouchMove, onTouchEnd, isActivated }
 */
export function useCornerTouch({ holdDuration = 1000, cornerThreshold = 50, onActivate } = {}) {
  const [isActivated, setIsActivated] = useState(false);
  const touchesRef = useRef(new Map()); // Track active touches
  const timerRef = useRef(null);
  const elementRectRef = useRef(null);
  const activatedRef = useRef(false);

  // Clear the timer
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Check if a touch point is in a corner
  const getCorner = useCallback((touch, rect) => {
    if (!rect) return null;
    
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    
    const isLeft = x <= cornerThreshold;
    const isRight = x >= rect.width - cornerThreshold;
    const isTop = y <= cornerThreshold;
    const isBottom = y >= rect.height - cornerThreshold;
    
    if (isLeft && isTop) return 'topLeft';
    if (isRight && isTop) return 'topRight';
    if (isLeft && isBottom) return 'bottomLeft';
    if (isRight && isBottom) return 'bottomRight';
    
    return null;
  }, [cornerThreshold]);

  // Check if all 4 corners are being touched
  const checkAllCorners = useCallback(() => {
    const corners = new Set();
    touchesRef.current.forEach((corner) => {
      if (corner) corners.add(corner);
    });
    
    return corners.size === 4 && 
           corners.has('topLeft') && 
           corners.has('topRight') && 
           corners.has('bottomLeft') && 
           corners.has('bottomRight');
  }, []);

  // Start the hold timer if all corners are touched
  const startTimerIfReady = useCallback(() => {
    clearTimer();
    
    if (checkAllCorners() && !activatedRef.current) {
      timerRef.current = setTimeout(() => {
        activatedRef.current = true;
        setIsActivated(true);
        onActivate?.();
        
        // Vibrate for feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(100);
        }
      }, holdDuration);
    }
  }, [checkAllCorners, clearTimer, holdDuration, onActivate]);

  // Handle touch start
  const onTouchStart = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    elementRectRef.current = rect;
    
    // Process all new touches
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const corner = getCorner(touch, rect);
      touchesRef.current.set(touch.identifier, corner);
    }
    
    startTimerIfReady();
  }, [getCorner, startTimerIfReady]);

  // Handle touch move - update corner detection as fingers move
  const onTouchMove = useCallback((e) => {
    const rect = elementRectRef.current;
    if (!rect) return;
    
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      const corner = getCorner(touch, rect);
      touchesRef.current.set(touch.identifier, corner);
    }
    
    // Re-check corners - if we lost one, clear timer
    if (!checkAllCorners()) {
      clearTimer();
    } else {
      startTimerIfReady();
    }
  }, [getCorner, checkAllCorners, clearTimer, startTimerIfReady]);

  // Handle touch end
  const onTouchEnd = useCallback((e) => {
    // Remove ended touches
    for (let i = 0; i < e.changedTouches.length; i++) {
      const touch = e.changedTouches[i];
      touchesRef.current.delete(touch.identifier);
    }
    
    // Clear timer if we no longer have all corners
    if (!checkAllCorners()) {
      clearTimer();
    }
  }, [checkAllCorners, clearTimer]);

  // Reset activation state (call this after handling the activation)
  const reset = useCallback(() => {
    activatedRef.current = false;
    setIsActivated(false);
    touchesRef.current.clear();
    clearTimer();
  }, [clearTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  return {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    isActivated,
    reset
  };
}
